import { INVITE_ERROR_TEXT, mapInviteError } from "@/api/invitations/mapInviteError";
import { isOpenInvitation } from "@/api/invitations/invitationState";
import type { OrganizationInvitation } from "@/api/invitations/InvitationsApi";
import {
  useInviteMember,
  useOrganizationInvitations,
  useResendInvite,
  useRevokeInvite,
} from "@/api/invitations/useInvitations";
import {
  ORG_MEMBERSHIP_ROLES,
  ORG_ROLE_LABELS,
  formatOrgRoles,
  type OrgMembershipRole,
} from "@/auth/membershipRoles";
import { ROLE_DESCRIPTIONS } from "@/auth/memberRoleEditor";
import { useOrgPermissions } from "@/auth/orgPermissions";
import { routes } from "@/constants/routes";
import { useTheme } from "@/theme/ThemeProvider";
import { ActionNotice } from "@/ui/components/ActionNotice";
import { AppButton } from "@/ui/components/AppButton";
import { EmptyState, LoadingState } from "@/ui/components/StateViews";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { WorkTabs } from "@/ui/components/WorkTabs";
import { confirmAction } from "@/ui/confirmAction";
import { formatTimestampDE, uiErrorMessage } from "@/utils/isoDate";
import { useLocalSearchParams, useRouter } from "expo-router";
import { CheckCircleIcon, LockSimpleIcon, PaperPlaneTiltIcon } from "phosphor-react-native";
import { useEffect, useState } from "react";
import { Pressable, Text, View } from "react-native";

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

const STATUS_LABEL: Record<string, string> = {
  open: "Offen",
  accepted: "Angenommen",
  revoked: "Widerrufen",
  expired: "Abgelaufen",
};

type Notice = { tone: "success" | "error" | "info"; text: string };

function displayStatus(row: OrganizationInvitation, nowMs: number): string {
  if (row.status === "open" && !isOpenInvitation(row, nowMs)) return "Abgelaufen";
  return STATUS_LABEL[row.status] ?? row.status;
}

export default function OrganizationInviteScreen() {
  const { tenantID: organizationId } = useLocalSearchParams<{ tenantID: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const permissions = useOrgPermissions(organizationId);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [roles, setRoles] = useState<OrgMembershipRole[]>(["caregiver"]);
  const [sent, setSent] = useState(false);
  const [tab, setTab] = useState("open");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const invite = useInviteMember(organizationId);
  const resend = useResendInvite(organizationId);
  const revoke = useRevokeInvite(organizationId);
  const history = useOrganizationInvitations(
    permissions.canManageMembers ? organizationId : undefined,
  );

  const canInviteAdmin = permissions.canChangeAdminRole || permissions.isSuperadmin;
  const canSubmit =
    Boolean(organizationId) &&
    EMAIL_PATTERN.test(email.trim()) &&
    displayName.trim().length > 0 &&
    roles.length > 0 &&
    !invite.isPending;

  useEffect(() => {
    if (history.error) console.error("invitations", history.error);
  }, [history.error]);

  const back = () => router.replace(routes.tenant.members(organizationId ?? "") as never);

  if (permissions.loading) {
    return (
      <Screen title="Mitglied einladen">
        <LoadingState message="Wird geladen …" />
      </Screen>
    );
  }

  if (!permissions.canManageMembers) {
    return (
      <Screen title="Mitglied einladen" scroll>
        <EmptyState
          title="Nur für Administration und Vorstand"
          message="Einladungen und Rollen verwalten dürfen nur Mitglieder mit der Rolle Administration oder Vorstand."
          action={
            <AppButton onPress={back} style={{ minHeight: 44 }}>
              Zur Mitgliederliste
            </AppButton>
          }
        />
      </Screen>
    );
  }

  const toggleRole = (role: OrgMembershipRole) => {
    if (role === "admin" && !canInviteAdmin) return;
    setRoles((current) => {
      if (current.includes(role)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== role);
      }
      return [...current, role];
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    setNotice(null);
    invite.mutate(
      { email, displayName, roles },
      { onSuccess: () => setSent(true) },
    );
  };

  const resetForm = () => {
    setEmail("");
    setDisplayName("");
    setRoles(["caregiver"]);
    setSent(false);
    invite.reset();
  };

  const sendAgain = async (row: OrganizationInvitation) => {
    if (pendingId) return;
    if (row.roles.includes("admin") && !canInviteAdmin) return;
    setPendingId(row.id);
    setNotice({ tone: "info", text: "Wird gesendet …" });
    try {
      await resend.mutateAsync(row.id);
      setNowMs(Date.now());
      setNotice({ tone: "info", text: `Einladung an ${row.email} erneut gesendet.` });
    } catch (error) {
      console.error("resend-invite", error);
      setNotice({
        tone: "error",
        text: uiErrorMessage(error, INVITE_ERROR_TEXT.generic),
      });
    } finally {
      setPendingId(null);
    }
  };

  const revokeRow = async (row: OrganizationInvitation) => {
    if (pendingId) return;
    const ok = await confirmAction({
      title: "Einladung widerrufen?",
      message: `Der Link für ${row.display_name} wird sofort ungültig. Du kannst die Person später erneut einladen.`,
      confirmLabel: "Widerrufen",
      cancelLabel: "Abbrechen",
    });
    if (!ok) return;
    setPendingId(row.id);
    try {
      await revoke.mutateAsync(row.id);
      setNowMs(Date.now());
      setNotice({ tone: "info", text: `Einladung an ${row.display_name} widerrufen.` });
    } catch (error) {
      console.error("revoke-invite", error);
      setNotice({
        tone: "error",
        text: uiErrorMessage(error, INVITE_ERROR_TEXT.generic),
      });
    } finally {
      setPendingId(null);
    }
  };

  const rows = history.data ?? [];
  const openRows = rows.filter((row) => isOpenInvitation(row, nowMs));
  const pastRows = rows.filter((row) => !isOpenInvitation(row, nowMs));
  const historyMessage = history.error
    ? mapInviteError({
        status: (history.error as { status?: number }).status ?? null,
        message: history.error instanceof Error ? history.error.message : null,
      })
    : null;
  const inviteMessage = invite.error
    ? uiErrorMessage(invite.error, INVITE_ERROR_TEXT.generic)
    : null;

  const renderRows = (list: OrganizationInvitation[]) => (
    <View style={{ gap: theme.space.md }}>
      {list.length === 0 && !history.isLoading ? (
        <Text style={{ color: theme.color.muted }}>Noch keine Einladungen.</Text>
      ) : null}
      {list.map((row) => {
        const open = isOpenInvitation(row, nowMs);
        const adminResendLocked = row.roles.includes("admin") && !canInviteAdmin;
        return (
          <View
            key={row.id}
            style={{
              gap: theme.space.xs,
              paddingBottom: theme.space.sm,
              borderBottomWidth: 1,
              borderBottomColor: theme.color.border,
            }}
          >
            <Text style={{ color: theme.color.text, fontWeight: "700" }}>{row.display_name}</Text>
            <Text style={{ color: theme.color.muted }}>{row.email}</Text>
            <Text style={{ color: theme.color.text }}>
              {displayStatus(row, nowMs)} · {formatOrgRoles(row.roles)}
            </Text>
            <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
              gültig bis {formatTimestampDE(row.expires_at)}
            </Text>
            {open ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.sm }}>
                <AppButton
                  variant="secondary"
                  onPress={() => void sendAgain(row)}
                  disabled={pendingId != null || adminResendLocked}
                  loading={pendingId === row.id && resend.isPending}
                  style={{ minHeight: 44 }}
                >
                  {pendingId === row.id && resend.isPending ? "Wird gesendet …" : "Erneut senden"}
                </AppButton>
                {adminResendLocked ? (
                  <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small, width: "100%" }}>
                    {INVITE_ERROR_TEXT.resendAdminRole}
                  </Text>
                ) : null}
                <AppButton
                  variant="danger"
                  onPress={() => void revokeRow(row)}
                  disabled={pendingId != null}
                  loading={pendingId === row.id && revoke.isPending}
                  style={{ minHeight: 44 }}
                >
                  Widerrufen
                </AppButton>
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );

  return (
    <Screen title="Mitglied einladen" scroll>
      {sent ? (
        <SectionCard title="Einladung gesendet">
          <View style={{ alignItems: "center", gap: theme.space.md, paddingVertical: theme.space.lg }}>
            <CheckCircleIcon size={48} color={theme.color.primary} weight="fill" />
            <Text style={{ color: theme.color.text, fontSize: theme.fontSize.body, textAlign: "center", maxWidth: 420 }}>
              Die Einladung ist sieben Tage gültig. Die Rollen sind fest und können von der
              eingeladenen Person nicht geändert werden.
            </Text>
          </View>
        </SectionCard>
      ) : (
        <SectionCard title="Neue Person einladen">
          <View style={{ gap: theme.space.md }}>
            <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.body }}>
              {permissions.orgName
                ? `Einladung in „${permissions.orgName}“. `
                : "Einladung in die Organisation. "}
              Neue Konten legen beim Annehmen ein Passwort fest. Bestehende Konten melden sich mit
              ihrem Passwort an.
            </Text>
            <FloatingLabelInput
              label="Name"
              required
              value={displayName}
              onChangeText={setDisplayName}
              autoCapitalize="words"
            />
            <FloatingLabelInput
              label="E-Mail-Adresse"
              required
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <View style={{ gap: theme.space.xs }}>
              <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small, fontWeight: "600" }}>
                Rollen
              </Text>
              {ORG_MEMBERSHIP_ROLES.map((role) => {
                const checked = roles.includes(role);
                const locked = role === "admin" && !canInviteAdmin;
                const labelColor = locked ? theme.color.buttonDisabledText : theme.color.text;
                return (
                  <Pressable
                    key={role}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked, disabled: locked }}
                    accessibilityLabel={`${ORG_ROLE_LABELS[role]}, ${ROLE_DESCRIPTIONS[role]}${locked ? ", gesperrt" : ""}`}
                    accessibilityHint={locked ? INVITE_ERROR_TEXT.adminRole : undefined}
                    disabled={locked}
                    hitSlop={8}
                    onPress={() => toggleRole(role)}
                    style={{
                      minHeight: 44,
                      paddingVertical: theme.space.sm,
                      paddingHorizontal: theme.space.sm,
                      borderRadius: theme.radius.md,
                      borderWidth: 1,
                      borderColor: locked ? theme.color.inputBorder : theme.color.border,
                      backgroundColor: locked ? theme.color.buttonDisabledBg : theme.color.surface,
                      gap: 2,
                    }}
                  >
                    <View style={{ flexDirection: "row", alignItems: "center", gap: theme.space.xs }}>
                      <Text style={{ color: labelColor, fontWeight: "700", flex: 1 }}>
                        {checked ? "✓ " : "○ "}
                        {ORG_ROLE_LABELS[role]}
                      </Text>
                      {locked ? (
                        <View
                          accessibilityElementsHidden
                          importantForAccessibility="no-hide-descendants"
                          style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
                        >
                          <LockSimpleIcon size={16} color={theme.color.buttonDisabledText} />
                          <Text style={{ color: theme.color.buttonDisabledText, fontWeight: "700" }}>
                            gesperrt
                          </Text>
                        </View>
                      ) : null}
                    </View>
                    <Text style={{ color: labelColor }}>{ROLE_DESCRIPTIONS[role]}</Text>
                    {locked ? (
                      <Text style={{ color: labelColor, fontSize: theme.fontSize.small }}>
                        {INVITE_ERROR_TEXT.adminRole}
                      </Text>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
            <ActionNotice
              notice={inviteMessage ? { tone: "error", text: inviteMessage } : null}
            />
            <AppButton
              icon={PaperPlaneTiltIcon}
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={invite.isPending}
              style={{ alignSelf: "flex-start", minHeight: 44 }}
            >
              {invite.isPending ? "Wird gesendet…" : "Einladung senden"}
            </AppButton>
          </View>
        </SectionCard>
      )}

      {sent ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.sm, marginTop: theme.space.md }}>
          <AppButton onPress={resetForm} style={{ minHeight: 44 }}>
            Weitere Person einladen
          </AppButton>
        </View>
      ) : null}

      <SectionCard title="Einladungen">
        <View style={{ gap: theme.space.md }}>
          {history.isLoading ? (
            <Text style={{ color: theme.color.muted }}>Wird geladen…</Text>
          ) : null}
          <ActionNotice
            notice={historyMessage ? { tone: "error", text: historyMessage } : null}
          />
          <ActionNotice notice={notice} />
          <WorkTabs
            activeId={tab}
            onSelect={setTab}
            tabs={[
              { id: "open", label: `Offen (${openRows.length})`, panel: renderRows(openRows) },
              { id: "past", label: `Verlauf (${pastRows.length})`, panel: renderRows(pastRows) },
            ]}
          />
        </View>
      </SectionCard>

      <AppButton
        variant="ghost"
        onPress={back}
        style={{ alignSelf: "flex-start", marginTop: theme.space.md, minHeight: 44 }}
      >
        Zur Mitgliederliste
      </AppButton>
    </Screen>
  );
}
